import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addWidgetToFilterList } from "../../../state/features/analytics/actions";
import TabularTable from "./table";
import Widget from "../../baseWidget";
import { useWidgetContext } from "../../../helpers/hooks/useWidgetContext";
import { RootState } from "../../../state/store";
import styled from "styled-components";
import {
  TbSortAscendingNumbers,
  TbSortDescendingNumbers,
} from "react-icons/tb";
import { useMeasure } from "react-use";

const SortControl = ({ sortAscending, handleSortModeSelection }) => {
  return (
    <SortButton onClick={handleSortModeSelection}>
      {sortAscending ? (
        <TbSortAscendingNumbers />
      ) : (
        <TbSortDescendingNumbers width={"14px"} />
      )}
    </SortButton>
  );
};

const TableWidget = () => {
  const [widgetData, setWidgetData] = useState<any>({});
  const [sortAscending, setSortAscending] = useState(false);
  const [widgetRef, contentDimensions] = useMeasure();
  const [widgetHeaderRef, headerDimensions] = useMeasure();
  const [rowLimit, setRowLimit] = useState(10);

  const widget = useWidgetContext();
  const dispatch = useDispatch();

  const { widgets: widgetFilters } = useSelector(
    (state: RootState) => state.filters
  );

  useEffect(() => {
    if (widget.widgetData.data) {
      const sortedWidgetData = sortWidgetData(widget.widgetData);
      setWidgetData(sortedWidgetData);
      const widgetFilterExists = widgetFilters.find(
        (i) => i.key == widget?.widgetFilterConfig?.widgetFilterKey
      );
      if (widget.widgetFilterConfig?.enableFilters && !widgetFilterExists) {
        dispatch(
          addWidgetToFilterList({
            name: widget.widgetFilterConfig.filterTitle,
            filterKey: widget.widgetFilterConfig.widgetFilterKey,
            options: widget.widgetData.data.map((d) => ({
              id: d[widget.widgetFilterConfig.widgetFilterKey],
              name: d[widget.widgetData.config.filter_by_accessor],
            })),
          })
        );
      }
    }
  }, [widget.widgetData]);

  const tableData = useMemo(() => {
    if (widget.widgetData.data) {
      const sortAccessor = widget.widgetData.config.sort_by_column_accessor;
      return widget.widgetData.data
        .sort((a, b) =>
          sortAscending
            ? a[sortAccessor] - b[sortAccessor]
            : b[sortAccessor] - a[sortAccessor]
        )
        .filter((d, i) => i < rowLimit);
    }
    return null;
  }, [sortAscending, rowLimit, widget.widgetData]);

  const sortWidgetData = (widgetData, ascending = sortAscending) => {
    let accessorSortingKey = widgetData.config.sort_by_column_accessor;
    let updatedRowData = widgetData.data.sort((a, b) => {
      if (!ascending) {
        return b[accessorSortingKey] - a[accessorSortingKey];
      }
      return a[accessorSortingKey] - b[accessorSortingKey];
    });
    return {
      ...widgetData,
      data: updatedRowData,
    };
  };

  const tableRowData = useMemo(() => {
    if (widgetData?.data) {
      return widgetData.data;
    }
    return [];
  }, [JSON.stringify(widgetData)]);

  return (
    <Widget ref={widgetRef}>
      <div className="widget-header" ref={widgetHeaderRef}>
        <Widget.Title>{widgetData.title}</Widget.Title>
        <Widget.Controls>
          {tableRowData.length > 0 && !widget.marketingHtmlUrl ? (
            <>
              <Widget.ExtendedAReportCta />
              <SortControl
                sortAscending={sortAscending}
                handleSortModeSelection={() => setSortAscending((s) => !s)}
              />
              <Widget.AiSuggestionCta />
            </>
          ) : null}
        </Widget.Controls>
      </div>
      <Widget.Content>
        {tableRowData.length > 0 ? (
          <TabularTable
            data={tableData}
            width={contentDimensions.width}
            height={contentDimensions.height - headerDimensions.height}
          />
        ) : (
          <Widget.EmptyData
            width={contentDimensions.width}
            height={contentDimensions.height - headerDimensions.height}
          />
        )}
      </Widget.Content>
    </Widget>
  );
};

export default TableWidget;

const SortButton = styled.div`
  box-sizing: border-box;
  height: 35px;
  width: 35px;
  border: 2px solid #58585e;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  padding: 2px 8px;
  padding: 0px;
  align-items: center;
`;
